import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  Package,
  ShoppingBag,
  ShoppingCart,
  ShoppingBasket,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@client/src/utils/logger';
import { orders } from '@client/src/api/index';
import { getOrderPlacers, getPlatformCountries } from '@client/src/config/env';
import type {
  Platform,
  ShippingStatus,
  WarehouseStatus,
  OrderRecord,
  OrderPlacer,
} from '@client/src/types/order';
import { Image } from '@client/src/components/ui/image';
import { Input } from '@client/src/components/ui/input';

interface FilterState {
  shippingStatus?: ShippingStatus;
  isRefunded?: boolean;
  warehouseStatus?: WarehouseStatus;
  country?: string;
  customerName?: string;
  orderPlacer?: OrderPlacer;
  orderDateMonth?: string;
}

const PAGE_SIZE = 21;

const PLATFORM_TABS: {
  key: Platform;
  label: string;
  icon: typeof Package;
}[] = [
  { key: 'depop', label: 'Depop', icon: ShoppingBag },
  { key: 'vinted', label: 'Vinted', icon: ShoppingCart },
  { key: 'poshmark', label: 'Poshmark', icon: ShoppingBasket },
  { key: 'ebay', label: 'Ebay', icon: Tag },
];

const PLATFORM_KEYS = PLATFORM_TABS.map((t) => t.key);
const STORAGE_KEY_ACTIVE_PLATFORM = 'order:activePlatform';

const readStoredPlatform = (): Platform => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY_ACTIVE_PLATFORM);
    if (stored && (PLATFORM_KEYS as string[]).indexOf(stored) >= 0) {
      return stored as Platform;
    }
  } catch (err) {
    logger.warn('读取持久化 platform 失败', err);
  }
  return 'depop';
};

const OrderListPage = () => {
  const navigate = useNavigate();

  const [platform, setPlatform] = useState<Platform>(() => readStoredPlatform());
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<FilterState>({});
  const [pendingFilters, setPendingFilters] = useState<FilterState>({});
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const [items, setItems] = useState<OrderRecord[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const countries = getPlatformCountries(platform);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch orders
  const fetchOrders = useCallback(
    async (pageNum: number, reset: boolean) => {
      setLoading(true);
      try {
        const params: Parameters<typeof orders.getOrders>[0] = {
          platform,
          page: pageNum,
          pageSize: PAGE_SIZE,
        };
        if (debouncedSearch.trim()) {
          params.trackingNumber = debouncedSearch.trim();
        }
        if (filters.shippingStatus !== undefined) {
          params.shippingStatus = filters.shippingStatus;
        }
        if (filters.isRefunded !== undefined) {
          params.isRefunded = filters.isRefunded;
        }
        if (filters.warehouseStatus !== undefined) {
          params.warehouseStatus = filters.warehouseStatus;
        }
        if (filters.country !== undefined) {
          params.country = filters.country;
        }
        if (filters.customerName !== undefined) {
          const trimmed: string = filters.customerName.trim();
          if (trimmed) params.customerName = trimmed;
        }
        if (filters.orderPlacer !== undefined) {
          params.orderPlacer = filters.orderPlacer;
        }
        if (filters.orderDateMonth !== undefined) {
          params.orderDateMonth = filters.orderDateMonth;
        }

        const response = await orders.getOrders(params);

        if (reset) {
          setItems(response.items);
        } else {
          setItems((prev: OrderRecord[]) => [...prev, ...response.items]);
        }
        setHasMore(response.items.length === PAGE_SIZE &&
          response.page * PAGE_SIZE < response.total);
        setPage(pageNum);
        setInitialLoaded(true);
      } catch (error) {
        logger.error('加载订单失败', error);
        toast.error('加载订单失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    },
    [platform, debouncedSearch, filters],
  );

  // Reset and reload when platform / search / filters change
  useEffect(() => {
    setItems([]);
    setHasMore(true);
    setPage(1);
    setInitialLoaded(false);
    fetchOrders(1, true);
  }, [platform, debouncedSearch, filters]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          hasMore &&
          !loading &&
          initialLoaded
        ) {
          fetchOrders(page + 1, false);
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchOrders, initialLoaded]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.shippingStatus !== undefined ||
      filters.isRefunded !== undefined ||
      filters.warehouseStatus !== undefined ||
      filters.country !== undefined ||
      filters.customerName !== undefined ||
      filters.orderPlacer !== undefined ||
      filters.orderDateMonth !== undefined
    );
  }, [filters]);

  const handlePlatformChange = (newPlatform: Platform) => {
    if (newPlatform === platform) return;
    try {
      window.localStorage.setItem(STORAGE_KEY_ACTIVE_PLATFORM, newPlatform);
    } catch (err) {
      logger.warn('持久化 platform 失败', err);
    }
    setPlatform(newPlatform);
    setSearchQuery('');
    setDebouncedSearch('');
    setFilters({});
    setPendingFilters({});
    setFilterPanelOpen(false);
  };

  const handleToggleFilterPanel = () => {
    if (!filterPanelOpen) {
      setPendingFilters({ ...filters });
    }
    setFilterPanelOpen((prev: boolean) => !prev);
  };

  const handleResetFilters = () => {
    setPendingFilters({});
    setFilters({});
    setFilterPanelOpen(false);
  };

  useEffect(() => {
    if (!filterPanelOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target as Node) &&
        filterBtnRef.current &&
        !filterBtnRef.current.contains(e.target as Node)
      ) {
        setFilterPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterPanelOpen]);

  const updatePendingFilters = (next: FilterState) => {
    setPendingFilters(next);
  };

  const confirmFilters = () => {
    setFilters(pendingFilters);
    setFilterPanelOpen(false);
  };

  const toggleShippingStatus = (status: ShippingStatus) => {
    updatePendingFilters({
      ...pendingFilters,
      shippingStatus:
        pendingFilters.shippingStatus === status ? undefined : status,
    });
  };

  const toggleIsRefunded = (value: boolean) => {
    updatePendingFilters({
      ...pendingFilters,
      isRefunded: pendingFilters.isRefunded === value ? undefined : value,
    });
  };

  const toggleWarehouseStatus = (status: WarehouseStatus) => {
    updatePendingFilters({
      ...pendingFilters,
      warehouseStatus:
        pendingFilters.warehouseStatus === status ? undefined : status,
    });
  };

  const toggleCountry = (country: string) => {
    updatePendingFilters({
      ...pendingFilters,
      country: pendingFilters.country === country ? undefined : country,
    });
  };

  const handleCustomerNameChange = (value: string) => {
    updatePendingFilters({
      ...pendingFilters,
      customerName: value || undefined,
    });
  };

  const toggleOrderPlacer = (placer: OrderPlacer) => {
    updatePendingFilters({
      ...pendingFilters,
      orderPlacer:
        pendingFilters.orderPlacer === placer ? undefined : placer,
    });
  };

  const handleOrderDateMonthChange = (value: string) => {
    updatePendingFilters({
      ...pendingFilters,
      orderDateMonth: value || undefined,
    });
  };

  const handleOrderClick = (id: string) => {
    navigate(`/orders/${id}`);
  };

  const handleFabClick = () => {
    navigate('/orders/new', { state: { platform } });
  };

  const renderPill = (
    label: string,
    active: boolean,
    onClick: () => void,
  ) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className={
        'px-4 py-1.5 rounded-full text-sm font-medium transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'bg-accent text-accent-foreground')
      }
    >
      {label}
    </button>
  );

  const showEmpty = initialLoaded && !loading && items.length === 0;

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* 标题栏 */}
      {/* <div className="px-4 py-4 sticky top-0 z-20 bg-background px-4 pt-4 pb-3 bg-white border-b border-border/30">
        <p className="text-base font-medium text-foreground text-center">订单列表</p>
      </div> */}
      {/* Search Bar + Filter Popover */}
      <div className="sticky top-0 z-20 bg-background px-4 pt-4 pb-3 bg-white">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
             <input
               type="text"
               placeholder="请输入运单号"
               value={searchQuery}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                 setSearchQuery(e.target.value)
               }
               className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
             />
          </div>
          <button
            ref={filterBtnRef}
            type="button"
            onClick={handleToggleFilterPanel}
            className={
              'relative flex items-center justify-center w-10 h-10 rounded-lg border shrink-0 transition-colors ' +
              (filterPanelOpen
                ? 'bg-primary/10 border-primary/30'
                : 'bg-card border-border')
            }
            aria-label="筛选"
            aria-expanded={filterPanelOpen}
          >
            <Filter className={
              'w-5 h-5 ' + (filterPanelOpen ? 'text-primary' : 'text-foreground')
            } />
            {hasActiveFilters && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
            )}
          </button>
        </div>

        {/* Filter Floating Panel */}
        {filterPanelOpen && (
          <div
            ref={filterPanelRef}
            className="absolute left-4 right-4 top-full mt-2 bg-card rounded-xl shadow-lg border border-border p-4 space-y-4 z-30 max-h-[70vh] overflow-y-auto"
          >
          {/* 发货状态 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              发货状态
            </p>
            <div className="flex gap-2">
              {renderPill(
                '未发',
                pendingFilters.shippingStatus === 'unshipped',
                () => toggleShippingStatus('unshipped'),
              )}
              {renderPill(
                '已发',
                pendingFilters.shippingStatus === 'shipped',
                () => toggleShippingStatus('shipped'),
              )}
            </div>
          </div>

          {/* 是否退款 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              是否退款
            </p>
            <div className="flex gap-2">
              {renderPill(
                '是',
                pendingFilters.isRefunded === true,
                () => toggleIsRefunded(true),
              )}
              {renderPill(
                '否',
                pendingFilters.isRefunded === false,
                () => toggleIsRefunded(false),
              )}
            </div>
          </div>

          {/* 到库状态 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              到库状态
            </p>
            <div className="flex gap-2">
              {renderPill(
                '已到',
                pendingFilters.warehouseStatus === 'arrived',
                () => toggleWarehouseStatus('arrived'),
              )}
              {renderPill(
                '未到',
                pendingFilters.warehouseStatus === 'not_arrived',
                () => toggleWarehouseStatus('not_arrived'),
              )}
            </div>
          </div>

          {/* 国家 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">国家</p>
            <div className="flex flex-wrap gap-2">
              {countries.map((country: string) =>
                renderPill(
                  country,
                  pendingFilters.country === country,
                  () => toggleCountry(country),
                ),
              )}
            </div>
          </div>

          {/* 客户名字 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              客户名字
            </p>
            <Input
              type="text"
              placeholder="输入客户名字搜索"
              value={pendingFilters.customerName || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCustomerNameChange(e.target.value)
              }
            />
          </div>

          {/* 下单人 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">下单人</p>
            <div className="flex flex-wrap gap-2">
              {getOrderPlacers(platform).map((placer: OrderPlacer) =>
                renderPill(
                  placer,
                  pendingFilters.orderPlacer === placer,
                  () => toggleOrderPlacer(placer),
                ),
              )}
            </div>
          </div>

          {/* 下单年月 */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">下单年月</p>
            <Input
              type="month"
              value={pendingFilters.orderDateMonth || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleOrderDateMonthChange(e.target.value)
              }
            />
          </div>

          {/* Action buttons */}
          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={handleResetFilters}
              className="flex-1 h-10 rounded-lg text-sm font-medium text-foreground bg-accent hover:bg-accent/80 transition-colors"
            >
              重置
            </button>
            <button
              type="button"
              onClick={confirmFilters}
              className="flex-1 h-10 rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
            >
              确认筛选
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Order Grid */}
      <div className="px-4 py-2">
        {showEmpty ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Package className="w-16 h-16 mb-3 opacity-40" />
            <p className="text-sm">暂无订单</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-3 gap-2"
            data-ai-section-type="card-list"
          >
             {items.map((order: OrderRecord) => (
               <button
                 key={order.id}
                 type="button"
                 onClick={() => handleOrderClick(order.id)}
                 className="relative flex flex-col rounded-lg overflow-hidden bg-card shadow-sm hover:opacity-90 transition-opacity active:scale-[0.98]"
               >
                 <div className="relative aspect-square bg-accent overflow-hidden">
                   {/* 红点：只有到货且到库(shipped + arrived)才不显示 */}
                   {!(order.shippingStatus === 'shipped' && order.warehouseStatus === 'arrived') && (
                     <span className="absolute top-1.5 right-1.5 z-10 w-2.5 h-2.5 rounded-full bg-[hsl(0,75%,55%)] ring-2 ring-card shadow-sm" />
                   )}
                   {order.imageUrl ? (
                     <Image
                       src={order.imageUrl}
                       alt={order.trackingNumber || '订单图片'}
                       loading="lazy"
                       className="w-full h-full object-cover"
                     />
                   ) : (
                     <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                       <Package className="w-8 h-8 opacity-40" />
                     </div>
                   )}
                 </div>
                 <div className="p-2">
                   {order.trackingNumber ? (
                    //  <p className="text-sm font-medium text-foreground truncate">
                    //    {order.trackingNumber}
                    //  </p>
                    <p className="text-sm text-muted-foreground truncate">
                       已发货
                     </p>
                   ) : (
                     <p className="text-sm text-muted-foreground truncate">
                       未发货
                     </p>
                   )}
                   {order.orderDate && (
                     <p className="text-xs text-muted-foreground truncate mt-0.5">
                       {order.orderDate}
                     </p>
                   )}
                 </div>
               </button>
             ))}
          </div>
        )}

        {/* Loading / No more sentinel */}
        <div ref={sentinelRef} className="py-6 flex justify-center">
          {loading && items.length > 0 && (
            <p className="text-sm text-muted-foreground">加载中...</p>
          )}
          {!hasMore && items.length > 0 && !loading && (
            <p className="text-sm text-muted-foreground">没有更多了</p>
          )}
        </div>
      </div>

      {/* Bottom Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border">
        <div className="mx-auto max-w-md flex">
          {PLATFORM_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = platform === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handlePlatformChange(tab.key)}
                className={
                  'flex-1 flex flex-col items-center justify-center h-14 relative transition-colors ' +
                  (active ? 'text-primary' : 'text-muted-foreground')
                }
              >
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-b-full" />
                )}
                <Icon className={`w-5 h-5 ${active ? 'fill-current' : ''}`} />
                <span className="text-xs mt-0.5">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* FAB */}
      <button
        type="button"
        onClick={handleFabClick}
        className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 active:scale-95 transition-all"
        aria-label="新增订单"
        data-ai-section-type="button"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
};

export default OrderListPage;
